import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminKnowledgeBasePage.css";

type View="ARTICLES"|"INSIGHTS";

type Usage={
  views:number;
  helpful:number;
  notHelpful:number;
  ticketConversions:number;
};

type Version={
  articleVersionId:string;
  articleKey:string;
  versionNo:number;
  status:"DRAFT"|"PUBLISHED"|"ARCHIVED"|string;
  category:string;
  title:string;
  summary:string;
  bodyText:string;
  keywords:string[];
  errorCodes:string[];
  actionPath?:string|null;
  audienceRoles:string[];
  source:string;
  createdAt:string;
  updatedAt:string;
  publishedAt?:string|null;
  createdBy?:string|null;
  publishedBy?:string|null;
  usage30d:Usage;
};

type Hotspot={
  category:string;
  ticketCount:number;
  publishedArticleCount:number;
};

type TopArticle={
  articleVersionId:string;
  articleKey:string;
  title:string;
  category:string;
  views:number;
  helpful:number;
  notHelpful:number;
  ticketConversions:number;
  helpfulRate?:number|null;
};

type Workspace={
  summary:{
    published:number;
    drafts:number;
    views30d:number;
    helpful30d:number;
    notHelpful30d:number;
    ticketConversions30d:number;
  };
  versions:Version[];
  ticketCategoryHotspots:Hotspot[];
  topArticles:TopArticle[];
};

const CATEGORIES=[
  ["GENERAL","일반 안내"],
  ["ACCOUNT_AUTH","로그인·계정"],
  ["ONBOARDING","학교 개통"],
  ["ASSESSMENT","진로검사"],
  ["BADGE_CREDENTIAL","디지털배지"],
  ["EMAIL_NOTIFICATION","이메일·알림"],
  ["REPORTING","리포트·보고"],
  ["PRIVACY_DATA","동의·개인정보"],
  ["IMPORT_MIGRATION","명단·데이터"],
  ["PERFORMANCE","속도·성능"],
  ["OUTAGE","서비스 장애"]
] as const;

function categoryLabel(value:string){
  return CATEGORIES.find(([key])=>key===value)?.[1] || value;
}
function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function parseList(value:string){
  return Array.from(new Set(value.split(/[,\n]/).map((item)=>item.trim()).filter(Boolean)));
}
function rate(value?:number|null){
  return value==null?"N/A":value.toLocaleString("ko-KR",{maximumFractionDigits:1})+"%";
}

export function AdminKnowledgeBasePage({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [view,setView]=useState<View>("ARTICLES");
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [selectedId,setSelectedId]=useState("");
  const [query,setQuery]=useState("");
  const [categoryFilter,setCategoryFilter]=useState("ALL");
  const [statusFilter,setStatusFilter]=useState("CURRENT");
  const [showCreate,setShowCreate]=useState(false);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  const [createKey,setCreateKey]=useState("");
  const [createCategory,setCreateCategory]=useState("GENERAL");
  const [createTitle,setCreateTitle]=useState("");
  const [createSummary,setCreateSummary]=useState("");
  const [createBody,setCreateBody]=useState("");
  const [createKeywords,setCreateKeywords]=useState("");
  const [createErrors,setCreateErrors]=useState("");
  const [createAction,setCreateAction]=useState("/support");

  const [editCategory,setEditCategory]=useState("GENERAL");
  const [editTitle,setEditTitle]=useState("");
  const [editSummary,setEditSummary]=useState("");
  const [editBody,setEditBody]=useState("");
  const [editKeywords,setEditKeywords]=useState("");
  const [editErrors,setEditErrors]=useState("");
  const [editAction,setEditAction]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_knowledge_base_workspace",{p_root_organization_id:rootOrganizationId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setWorkspace(next);
      setSelectedId((current)=>
        next.versions.some((item)=>item.articleVersionId===current)
          ? current
          : next.versions.find((item)=>item.status==="DRAFT")?.articleVersionId
            || next.versions.find((item)=>item.status==="PUBLISHED")?.articleVersionId
            || ""
      );
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,refresh]);

  const selected=useMemo(
    ()=>workspace?.versions.find((item)=>item.articleVersionId===selectedId) || null,
    [workspace,selectedId]
  );

  useEffect(()=>{
    if(!selected)return;
    setEditCategory(selected.category);
    setEditTitle(selected.title);
    setEditSummary(selected.summary);
    setEditBody(selected.bodyText);
    setEditKeywords(selected.keywords.join(", "));
    setEditErrors(selected.errorCodes.join(", "));
    setEditAction(selected.actionPath || "");
  },[selected?.articleVersionId,selected?.updatedAt]);

  const articleGroups=useMemo(()=>{
    const map=new Map<string,Version[]>();
    for(const version of workspace?.versions || []){
      map.set(version.articleKey,[...(map.get(version.articleKey)||[]),version]);
    }
    return Array.from(map.entries()).map(([articleKey,versions])=>{
      const ordered=[...versions].sort((a,b)=>b.versionNo-a.versionNo);
      const current=ordered.find((item)=>item.status==="DRAFT")
        || ordered.find((item)=>item.status==="PUBLISHED")
        || ordered[0];
      return {articleKey,versions:ordered,current};
    });
  },[workspace]);

  const visibleGroups=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return articleGroups.filter((group)=>{
      const v=group.current;
      if(categoryFilter!=="ALL" && v.category!==categoryFilter)return false;
      if(statusFilter==="DRAFT" && !group.versions.some((item)=>item.status==="DRAFT"))return false;
      if(statusFilter==="PUBLISHED" && !group.versions.some((item)=>item.status==="PUBLISHED"))return false;
      return !keyword || [v.title,v.summary,v.articleKey,...v.keywords,...v.errorCodes]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[articleGroups,query,categoryFilter,statusFilter]);

  const selectedHistory=useMemo(
    ()=>selected ? (articleGroups.find((group)=>group.articleKey===selected.articleKey)?.versions || []) : [],
    [articleGroups,selected]
  );

  async function run(key:string,action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function createArticle(){
    const data=await run("create",()=>client.rpc("admin_create_knowledge_article",{
      p_root_organization_id:rootOrganizationId,
      p_article_key:createKey.trim().toUpperCase(),
      p_category:createCategory,
      p_title:createTitle.trim(),
      p_summary:createSummary.trim(),
      p_body_text:createBody.trim(),
      p_keywords:parseList(createKeywords),
      p_error_codes:parseList(createErrors).map((item)=>item.toUpperCase()),
      p_action_path:createAction.trim() || null,
      p_audience_roles:["CAREER_TEACHER","SCHOOL_ADMIN"]
    }),"새 Knowledge Article Draft를 만들었습니다.");
    if(data){
      setSelectedId((data as {articleVersionId:string}).articleVersionId);
      setShowCreate(false);
      setCreateKey("");setCreateTitle("");setCreateSummary("");setCreateBody("");setCreateKeywords("");setCreateErrors("");
    }
  }

  async function saveDraft(){
    if(!selected || selected.status!=="DRAFT")return;
    await run("save",()=>client.rpc("admin_update_knowledge_article_version",{
      p_root_organization_id:rootOrganizationId,
      p_article_version_id:selected.articleVersionId,
      p_category:editCategory,
      p_title:editTitle.trim(),
      p_summary:editSummary.trim(),
      p_body_text:editBody.trim(),
      p_keywords:parseList(editKeywords),
      p_error_codes:parseList(editErrors).map((item)=>item.toUpperCase()),
      p_action_path:editAction.trim() || null,
      p_audience_roles:["CAREER_TEACHER","SCHOOL_ADMIN"]
    }),"Draft를 저장했습니다.");
  }

  async function publish(){
    if(!selected || selected.status!=="DRAFT")return;
    await run("publish",()=>client.rpc("admin_publish_knowledge_article_version",{
      p_root_organization_id:rootOrganizationId,
      p_article_version_id:selected.articleVersionId
    }),selected.articleKey+" v"+selected.versionNo+"를 게시했습니다.");
  }

  async function newVersion(){
    if(!selected)return;
    const data=await run("version",()=>client.rpc("admin_create_knowledge_article_version",{
      p_root_organization_id:rootOrganizationId,
      p_article_key:selected.articleKey
    }),"새 Draft 버전을 만들었습니다.");
    if(data)setSelectedId((data as {articleVersionId:string}).articleVersionId);
  }

  if(!workspace)return <p className="muted">지식센터를 불러오는 중…</p>;

  const s=workspace.summary;
  const helpfulRate=(s.helpful30d+s.notHelpful30d)===0?null:s.helpful30d*100/(s.helpful30d+s.notHelpful30d);

  return (
    <section className="admin-kb-page">
      <header className="admin-kb-hero">
        <div>
          <p className="eyebrow">KNOWLEDGE BASE & GUIDED SUPPORT CENTER</p>
          <h2>지식센터·Guided Support</h2>
          <p>교사용 FAQ와 오류코드 가이드를 버전으로 관리하고, 실제 도움됨률과 Service Desk 전환을 이용해 문서 품질을 개선합니다.</p>
        </div>
        <button className="primary" onClick={()=>setShowCreate((value)=>!value)}>{showCreate?"작성 닫기":"+ 새 문서"}</button>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="kb-kpis">
        <Kpi label="게시 문서" value={s.published} />
        <Kpi label="Draft" value={s.drafts} />
        <Kpi label="30일 조회" value={s.views30d} />
        <Kpi label="도움됨" value={s.helpful30d} />
        <Kpi label="도움 안 됨" value={s.notHelpful30d} tone={s.notHelpful30d>0?"warn":"normal"} />
        <Kpi label="도움됨률" value={rate(helpfulRate)} />
        <Kpi label="티켓 전환" value={s.ticketConversions30d} />
      </div>

      <nav className="kb-tabs">
        <button className={view==="ARTICLES"?"active":""} onClick={()=>setView("ARTICLES")}>문서·버전 관리</button>
        <button className={view==="INSIGHTS"?"active":""} onClick={()=>setView("INSIGHTS")}>Guided Support 분석</button>
      </nav>

      {showCreate && (
        <section className="kb-card kb-create-card">
          <div className="kb-section-head"><div><p className="eyebrow">NEW ARTICLE</p><h3>새 문서 Draft</h3></div><span>Article Key는 게시 후 식별자로 유지됩니다.</span></div>
          <div className="kb-form-grid">
            <label>Article Key<input value={createKey} onChange={(e)=>setCreateKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,""))} placeholder="EXAMPLE_GUIDE" /></label>
            <label>Category<select value={createCategory} onChange={(e)=>setCreateCategory(e.target.value)}>{CATEGORIES.map(([key,label])=><option key={key} value={key}>{label}</option>)}</select></label>
            <label className="wide">제목<input value={createTitle} onChange={(e)=>setCreateTitle(e.target.value)} /></label>
            <label className="wide">요약<input value={createSummary} onChange={(e)=>setCreateSummary(e.target.value)} /></label>
            <label className="wide">본문<textarea rows={8} value={createBody} onChange={(e)=>setCreateBody(e.target.value)} /></label>
            <label>키워드<input value={createKeywords} onChange={(e)=>setCreateKeywords(e.target.value)} placeholder="쉼표로 구분" /></label>
            <label>오류코드<input value={createErrors} onChange={(e)=>setCreateErrors(e.target.value)} placeholder="WAITING_CONTACT, ..." /></label>
            <label className="wide">업무 바로가기<input value={createAction} onChange={(e)=>setCreateAction(e.target.value)} placeholder="/support" /></label>
          </div>
          <button className="primary" disabled={busy!==""||!createKey||!createTitle.trim()||!createSummary.trim()||!createBody.trim()} onClick={createArticle}>{busy==="create"?"생성 중…":"Draft 생성"}</button>
        </section>
      )}

      {view==="ARTICLES" && (
        <>
          <div className="kb-filterbar">
            <input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="제목·Article Key·키워드·오류코드 검색" />
            <select value={categoryFilter} onChange={(e)=>setCategoryFilter(e.target.value)}>
              <option value="ALL">전체 Category</option>
              {CATEGORIES.map(([key,label])=><option key={key} value={key}>{label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
              <option value="CURRENT">현재 문서</option>
              <option value="DRAFT">Draft 있음</option>
              <option value="PUBLISHED">게시됨</option>
            </select>
          </div>

          <div className="kb-layout">
            <aside className="kb-article-list">
              <div className="panel-title"><strong>Knowledge Articles</strong><span>{visibleGroups.length}개</span></div>
              {visibleGroups.map((group)=>(
                <button key={group.articleKey} className={selected?.articleKey===group.articleKey?"active":""} onClick={()=>setSelectedId(group.current.articleVersionId)}>
                  <div>
                    <span className="kb-category">{categoryLabel(group.current.category)}</span>
                    <span className={"kb-status "+group.current.status.toLowerCase()}>{group.current.status}</span>
                  </div>
                  <strong>{group.current.title}</strong>
                  <p>{group.current.summary}</p>
                  <small>{group.articleKey} · v{group.current.versionNo} · {group.versions.length} versions</small>
                </button>
              ))}
            </aside>

            <main className="kb-editor">
              {selected ? (
                <>
                  <header className="kb-editor-head">
                    <div>
                      <div className="kb-editor-tags">
                        <span className="kb-category">{categoryLabel(selected.category)}</span>
                        <span className={"kb-status "+selected.status.toLowerCase()}>{selected.status}</span>
                        <span>v{selected.versionNo}</span>
                      </div>
                      <h2>{selected.articleKey}</h2>
                      <p>{selected.source==="SYSTEM_SEED"?"System Seed":"Authored"} · 최근 변경 {fmt(selected.updatedAt)}</p>
                    </div>
                    <div className="kb-editor-actions">
                      {selected.status==="PUBLISHED" && <button className="secondary" disabled={busy!==""} onClick={newVersion}>{busy==="version"?"생성 중…":"새 버전 Draft"}</button>}
                      {selected.status==="DRAFT" && <button className="primary" disabled={busy!==""} onClick={publish}>{busy==="publish"?"게시 중…":"Publish"}</button>}
                    </div>
                  </header>

                  <div className="kb-version-history">
                    {selectedHistory.map((version)=>(
                      <button key={version.articleVersionId} className={selected.articleVersionId===version.articleVersionId?"active":""} onClick={()=>setSelectedId(version.articleVersionId)}>
                        v{version.versionNo} · {version.status}
                      </button>
                    ))}
                  </div>

                  <div className="kb-usage-strip">
                    <div><span>30일 조회</span><strong>{selected.usage30d.views}</strong></div>
                    <div><span>도움됨</span><strong>{selected.usage30d.helpful}</strong></div>
                    <div><span>도움 안 됨</span><strong>{selected.usage30d.notHelpful}</strong></div>
                    <div><span>티켓 전환</span><strong>{selected.usage30d.ticketConversions}</strong></div>
                  </div>

                  <section className="kb-card kb-editor-card">
                    <div className="kb-form-grid">
                      <label>Category<select disabled={selected.status!=="DRAFT"} value={editCategory} onChange={(e)=>setEditCategory(e.target.value)}>{CATEGORIES.map(([key,label])=><option key={key} value={key}>{label}</option>)}</select></label>
                      <label>업무 바로가기<input disabled={selected.status!=="DRAFT"} value={editAction} onChange={(e)=>setEditAction(e.target.value)} /></label>
                      <label className="wide">제목<input disabled={selected.status!=="DRAFT"} value={editTitle} onChange={(e)=>setEditTitle(e.target.value)} /></label>
                      <label className="wide">요약<input disabled={selected.status!=="DRAFT"} value={editSummary} onChange={(e)=>setEditSummary(e.target.value)} /></label>
                      <label className="wide">본문<textarea disabled={selected.status!=="DRAFT"} rows={12} value={editBody} onChange={(e)=>setEditBody(e.target.value)} /></label>
                      <label>키워드<input disabled={selected.status!=="DRAFT"} value={editKeywords} onChange={(e)=>setEditKeywords(e.target.value)} /></label>
                      <label>오류코드<input disabled={selected.status!=="DRAFT"} value={editErrors} onChange={(e)=>setEditErrors(e.target.value)} /></label>
                    </div>
                    {selected.status==="DRAFT" ? (
                      <button className="primary" disabled={busy!==""||!editTitle.trim()||!editSummary.trim()||!editBody.trim()} onClick={saveDraft}>{busy==="save"?"저장 중…":"Draft 저장"}</button>
                    ):(
                      <p className="kb-lock-note">게시된 버전은 수정하지 않습니다. 내용 변경이 필요하면 새 버전 Draft를 생성하세요.</p>
                    )}
                  </section>
                </>
              ):<p className="case-empty">문서를 선택하세요.</p>}
            </main>
          </div>
        </>
      )}

      {view==="INSIGHTS" && (
        <div className="kb-insights-grid">
          <section className="kb-card">
            <div className="kb-section-head"><div><p className="eyebrow">TOP ARTICLES</p><h3>실제 사용 문서</h3></div><span>최근 30일</span></div>
            <div className="kb-top-table">
              <div className="kb-top-row head"><span>문서</span><span>조회</span><span>도움됨률</span><span>티켓 전환</span></div>
              {workspace.topArticles.map((article)=>(
                <div className="kb-top-row" key={article.articleVersionId}>
                  <span><strong>{article.title}</strong><small>{categoryLabel(article.category)}</small></span>
                  <span>{article.views}</span>
                  <span>{rate(article.helpfulRate)}</span>
                  <span>{article.ticketConversions}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="kb-card">
            <div className="kb-section-head"><div><p className="eyebrow">CONTENT GAPS</p><h3>반복 문의 대비 문서 커버리지</h3></div><span>Service Desk 30일</span></div>
            <div className="kb-gap-list">
              {workspace.ticketCategoryHotspots.map((item)=>(
                <article key={item.category} className={item.publishedArticleCount===0?"gap":""}>
                  <div><strong>{categoryLabel(item.category)}</strong><small>최근 티켓 {item.ticketCount}건</small></div>
                  <span>{item.publishedArticleCount}개 문서</span>
                </article>
              ))}
              {!workspace.ticketCategoryHotspots.length && <p className="case-empty">최근 30일 Service Desk 티켓이 없습니다.</p>}
            </div>
          </section>

          <section className="kb-card kb-guided-flow">
            <div className="kb-section-head"><div><p className="eyebrow">DEFLECTION FLOW</p><h3>Guided Support 운영지표</h3></div></div>
            <div>
              <span>VIEWED</span><i>→</i><span>HELPFUL / NOT_HELPFUL</span><i>→</i><span>TICKET_STARTED</span><i>→</i><span>TICKET_CREATED</span>
            </div>
            <p>검색어 원문은 저장하지 않습니다. 문서 열람·피드백·티켓 전환 이벤트만 집계해 FAQ 품질을 개선합니다.</p>
          </section>
        </div>
      )}
    </section>
  );
}

function Kpi({label,value,tone="normal"}:{label:string;value:number|string;tone?:"normal"|"warn"}){
  return <div className={"kb-kpi "+tone}><span>{label}</span><strong>{value}</strong></div>;
}
